/**
 * FleetTracko Admin Dashboard - Main App
 */

import { Suspense, lazy, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';

import { Layout, RoleGuard } from './components';
import { PageLoading } from './components/ui';
import { ForgotPasswordPage, LoginPage, ProtectedRoute, ResetPasswordPage } from './features/auth';
import { useAuthStore, useUIStore } from './stores';

// Lazy load features for better performance
const DashboardPage = lazy(() =>
  import('./features/dashboard').then((m) => ({ default: m.DashboardPage })),
);
const DispatcherDashboard = lazy(() =>
  import('./features/dashboard').then((m) => ({ default: m.DispatcherDashboard })),
);
const YardOverviewPage = lazy(() =>
  import('./features/yard').then((m) => ({ default: m.YardOverviewPage })),
);
const YardMapPage = lazy(() => import('./features/yard').then((m) => ({ default: m.YardMapPage })));
const VehicleListPage = lazy(() =>
  import('./features/vehicles').then((m) => ({ default: m.VehicleListPage })),
);
const VehicleDetailPage = lazy(() =>
  import('./features/vehicles').then((m) => ({ default: m.VehicleDetailPage })),
);
const CheckInWizardPage = lazy(() =>
  import('./features/vehicles').then((m) => ({ default: m.CheckInWizardPage })),
);
const TaskListPage = lazy(() =>
  import('./features/tasks').then((m) => ({ default: m.TaskListPage })),
);
const TaskDetailPage = lazy(() =>
  import('./features/tasks').then((m) => ({ default: m.TaskDetailPage })),
);
const MyTasksPage = lazy(() =>
  import('./features/tasks').then((m) => ({ default: m.MyTasksPage })),
);
const YardWorkerQueuePage = lazy(() =>
  import('./features/tasks').then((m) => ({ default: m.YardWorkerQueuePage })),
);
const CleaningQueuePage = lazy(() =>
  import('./features/tasks').then((m) => ({ default: m.CleaningQueuePage })),
);
const QCInspectionQueuePage = lazy(() =>
  import('./features/tasks').then((m) => ({ default: m.QCInspectionQueuePage })),
);
const WorkshopQueuePage = lazy(() =>
  import('./features/tasks').then((m) => ({ default: m.WorkshopQueuePage })),
);
const TaskCreatePage = lazy(() =>
  import('./features/tasks').then((m) => ({ default: m.TaskCreatePage })),
);
const WorkflowListPage = lazy(() =>
  import('./features/workflows').then((m) => ({ default: m.WorkflowListPage })),
);
const WorkflowDetailPage = lazy(() =>
  import('./features/workflows').then((m) => ({ default: m.WorkflowDetailPage })),
);
const WorkflowCreatePage = lazy(() =>
  import('./features/workflows').then((m) => ({ default: m.WorkflowCreatePage })),
);
const WorkflowEditPage = lazy(() =>
  import('./features/workflows').then((m) => ({ default: m.WorkflowEditPage })),
);
const ServiceIntervalManagementPage = lazy(() =>
  import('./features/service-intervals').then((m) => ({
    default: m.ServiceIntervalManagementPage,
  })),
);
const ServiceCatalogPage = lazy(() =>
  import('./features/service-catalog').then((m) => ({ default: m.ServiceCatalogPage })),
);
const ServiceTypesPage = lazy(() =>
  import('./features/service-catalog').then((m) => ({ default: m.ServiceTypesPage })),
);
const CustomerListPage = lazy(() =>
  import('./features/customers').then((m) => ({ default: m.CustomerListPage })),
);
const CustomerDetailPage = lazy(() =>
  import('./features/customers').then((m) => ({ default: m.CustomerDetailPage })),
);
const CustomerEditPage = lazy(() =>
  import('./features/customers').then((m) => ({ default: m.CustomerEditPage })),
);
const CustomerCreatePage = lazy(() =>
  import('./features/customers').then((m) => ({ default: m.CustomerCreatePage })),
);
const UserListPage = lazy(() =>
  import('./features/users').then((m) => ({ default: m.UserListPage })),
);
const UserDetailPage = lazy(() =>
  import('./features/users').then((m) => ({ default: m.UserDetailPage })),
);
const UserEditPage = lazy(() =>
  import('./features/users').then((m) => ({ default: m.UserEditPage })),
);
const UserCreatePage = lazy(() =>
  import('./features/users').then((m) => ({ default: m.UserCreatePage })),
);
const ReportsPage = lazy(() =>
  import('./features/reports').then((m) => ({ default: m.ReportsPage })),
);
const InvoiceListPage = lazy(() =>
  import('./features/invoices').then((m) => ({ default: m.InvoiceListPage })),
);
const InvoiceDetailPage = lazy(() =>
  import('./features/invoices').then((m) => ({ default: m.InvoiceDetailPage })),
);
const InvoiceCreatePage = lazy(() =>
  import('./features/invoices').then((m) => ({ default: m.InvoiceCreatePage })),
);
const KVAListPage = lazy(() => import('./features/kva').then((m) => ({ default: m.KVAListPage })));
const KVADetailPage = lazy(() =>
  import('./features/kva').then((m) => ({ default: m.KVADetailPage })),
);
const KVACreatePage = lazy(() =>
  import('./features/kva').then((m) => ({ default: m.KVACreatePage })),
);
const DamageCaseListPage = lazy(() =>
  import('./features/damage').then((m) => ({ default: m.DamageCaseListPage })),
);
const DamageCaseDetailPage = lazy(() =>
  import('./features/damage').then((m) => ({ default: m.DamageCaseDetailPage })),
);
const DamageCaseCreatePage = lazy(() =>
  import('./features/damage').then((m) => ({ default: m.DamageCaseCreatePage })),
);
const RepairOrderListPage = lazy(() =>
  import('./features/repair').then((m) => ({ default: m.RepairOrderListPage })),
);
const RepairOrderDetailPage = lazy(() =>
  import('./features/repair').then((m) => ({ default: m.RepairOrderDetailPage })),
);
const RepairOrderCreatePage = lazy(() =>
  import('./features/repair').then((m) => ({ default: m.RepairOrderCreatePage })),
);
const InsuranceCaseListPage = lazy(() =>
  import('./features/insurance').then((m) => ({ default: m.InsuranceCaseListPage })),
);
const InsuranceCaseDetailPage = lazy(() =>
  import('./features/insurance').then((m) => ({ default: m.InsuranceCaseDetailPage })),
);
const InsuranceCaseCreatePage = lazy(() =>
  import('./features/insurance').then((m) => ({ default: m.InsuranceCaseCreatePage })),
);
const SettingsPage = lazy(() =>
  import('./features/settings').then((m) => ({ default: m.SettingsPage })),
);
const YardZoneListPage = lazy(() =>
  import('./features/settings').then((m) => ({ default: m.YardZoneListPage })),
);
const YardPositionListPage = lazy(() =>
  import('./features/settings').then((m) => ({ default: m.YardPositionListPage })),
);
const LocationListPage = lazy(() =>
  import('./features/settings').then((m) => ({ default: m.LocationListPage })),
);
const VehicleModelCatalogPage = lazy(() =>
  import('./features/settings').then((m) => ({ default: m.VehicleModelCatalogPage })),
);
const JobsPage = lazy(() => import('./features/system').then((m) => ({ default: m.JobsPage })));
const ContractListPage = lazy(() =>
  import('./features/contracts/ContractListPage').then((m) => ({ default: m.ContractListPage })),
);

// Not Found Page
function NotFoundPage() {
  return (
    <div className="flex items-center justify-center min-h-96">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-gray-200 dark:text-gray-700">404</h1>
        <p className="text-xl text-gray-600 dark:text-gray-400 mt-4">Seite nicht gefunden</p>
        <a
          href="/"
          className="mt-6 inline-block px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Zum Leitstand
        </a>
      </div>
    </div>
  );
}

// Unauthorized Page
function UnauthorizedPage() {
  return (
    <div className="flex items-center justify-center min-h-96">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-gray-200 dark:text-gray-700">403</h1>
        <p className="text-xl text-gray-600 dark:text-gray-400 mt-4">Zugriff verweigert</p>
        <p className="text-sm text-gray-500 mt-2">Sie haben keine Berechtigung für diese Seite.</p>
        <a
          href="/"
          className="mt-6 inline-block px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Zum Leitstand
        </a>
      </div>
    </div>
  );
}

function App() {
  const { refreshUser } = useAuthStore();
  const { darkMode } = useUIStore();

  // Apply dark mode class
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Refresh user on app load
  useEffect(() => {
    if (localStorage.getItem('accessToken')) {
      refreshUser();
    }
  }, [refreshUser]);

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        {/* Dashboard / Leitstand */}
        <Route
          index
          element={
            <Suspense fallback={<PageLoading />}>
              <DashboardPage />
            </Suspense>
          }
        />
        <Route
          path="dispatcher"
          element={
            <ProtectedRoute roles={['DISPATCHER', 'ADMIN', 'SUPER_ADMIN']}>
              <Suspense fallback={<PageLoading />}>
                <DispatcherDashboard />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Yard Management - Yard workers and supervisors */}
        <Route
          path="yard"
          element={
            <RoleGuard
              allowedRoles={['YARD_WORKER', 'DISPATCHER', 'ADMIN', 'SUPER_ADMIN']}
              redirectTo="/"
            >
              <Suspense fallback={<PageLoading />}>
                <YardOverviewPage />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="yard/map"
          element={
            <RoleGuard
              allowedRoles={['YARD_WORKER', 'DISPATCHER', 'ADMIN', 'SUPER_ADMIN']}
              redirectTo="/"
            >
              <Suspense fallback={<PageLoading />}>
                <YardMapPage />
              </Suspense>
            </RoleGuard>
          }
        />

        {/* Vehicle Management */}
        <Route
          path="vehicles"
          element={
            <Suspense fallback={<PageLoading />}>
              <VehicleListPage />
            </Suspense>
          }
        />
        <Route
          path="vehicles/checkin"
          element={
            <RoleGuard
              allowedRoles={['YARD_WORKER', 'DISPATCHER', 'ADMIN', 'SUPER_ADMIN']}
              redirectTo="/vehicles"
            >
              <Suspense fallback={<PageLoading />}>
                <CheckInWizardPage />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="vehicles/:id"
          element={
            <Suspense fallback={<PageLoading />}>
              <VehicleDetailPage />
            </Suspense>
          }
        />

        {/* Task Management */}
        <Route
          path="tasks"
          element={
            <Suspense fallback={<PageLoading />}>
              <TaskListPage />
            </Suspense>
          }
        />
        <Route
          path="tasks/new"
          element={
            <Suspense fallback={<PageLoading />}>
              <TaskCreatePage />
            </Suspense>
          }
        />
        <Route
          path="tasks/my"
          element={
            <Suspense fallback={<PageLoading />}>
              <MyTasksPage />
            </Suspense>
          }
        />
        <Route
          path="tasks/queue"
          element={
            <RoleGuard
              allowedRoles={['YARD_WORKER', 'DISPATCHER', 'ADMIN', 'SUPER_ADMIN']}
              redirectTo="/tasks"
            >
              <Suspense fallback={<PageLoading />}>
                <YardWorkerQueuePage />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="tasks/cleaning"
          element={
            <RoleGuard
              allowedRoles={['CLEANING', 'DISPATCHER', 'ADMIN', 'SUPER_ADMIN']}
              redirectTo="/tasks"
            >
              <Suspense fallback={<PageLoading />}>
                <CleaningQueuePage />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="tasks/qc"
          element={
            <RoleGuard
              allowedRoles={['QC_INSPECTOR', 'DISPATCHER', 'ADMIN', 'SUPER_ADMIN']}
              redirectTo="/tasks"
            >
              <Suspense fallback={<PageLoading />}>
                <QCInspectionQueuePage />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="tasks/workshop"
          element={
            <RoleGuard
              allowedRoles={['WORKSHOP', 'DISPATCHER', 'ADMIN', 'SUPER_ADMIN']}
              redirectTo="/tasks"
            >
              <Suspense fallback={<PageLoading />}>
                <WorkshopQueuePage />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="tasks/:id"
          element={
            <Suspense fallback={<PageLoading />}>
              <TaskDetailPage />
            </Suspense>
          }
        />

        {/* Workflow Management - Admin only */}
        <Route
          path="workflows"
          element={
            <RoleGuard allowedRoles={['ADMIN', 'SUPER_ADMIN']} redirectTo="/">
              <Suspense fallback={<PageLoading />}>
                <WorkflowListPage />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="workflows/new"
          element={
            <RoleGuard allowedRoles={['ADMIN', 'SUPER_ADMIN']} redirectTo="/">
              <Suspense fallback={<PageLoading />}>
                <WorkflowCreatePage />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="workflows/:id/edit"
          element={
            <RoleGuard allowedRoles={['ADMIN', 'SUPER_ADMIN']} redirectTo="/">
              <Suspense fallback={<PageLoading />}>
                <WorkflowEditPage />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="workflows/:id"
          element={
            <RoleGuard allowedRoles={['ADMIN', 'SUPER_ADMIN']} redirectTo="/">
              <Suspense fallback={<PageLoading />}>
                <WorkflowDetailPage />
              </Suspense>
            </RoleGuard>
          }
        />

        {/* Service Interval Management (Wiederkehrende Services) */}
        <Route
          path="service-intervals"
          element={
            <RoleGuard allowedRoles={['ADMIN', 'SUPER_ADMIN']} redirectTo="/">
              <Suspense fallback={<PageLoading />}>
                <ServiceIntervalManagementPage />
              </Suspense>
            </RoleGuard>
          }
        />
        {/* Service Catalog (unified Service model) */}
        <Route
          path="service-catalog"
          element={
            <RoleGuard allowedRoles={['ADMIN', 'SUPER_ADMIN']} redirectTo="/">
              <Suspense fallback={<PageLoading />}>
                <ServiceCatalogPage />
              </Suspense>
            </RoleGuard>
          }
        />
        {/* System-Jobs (Cron-Observability & -Steuerung) */}
        <Route
          path="system/jobs"
          element={
            <RoleGuard allowedRoles={['ADMIN', 'SUPER_ADMIN']} redirectTo="/">
              <Suspense fallback={<PageLoading />}>
                <JobsPage />
              </Suspense>
            </RoleGuard>
          }
        />
        {/* Service Types (managed categories) */}
        <Route
          path="service-types"
          element={
            <RoleGuard allowedRoles={['ADMIN', 'SUPER_ADMIN']} redirectTo="/">
              <Suspense fallback={<PageLoading />}>
                <ServiceTypesPage />
              </Suspense>
            </RoleGuard>
          }
        />

        {/* Customer Management */}
        <Route
          path="customers"
          element={
            <RoleGuard allowedRoles={['DISPATCHER', 'ADMIN', 'SUPER_ADMIN']} redirectTo="/">
              <Suspense fallback={<PageLoading />}>
                <CustomerListPage />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="customers/new"
          element={
            <RoleGuard allowedRoles={['DISPATCHER', 'ADMIN', 'SUPER_ADMIN']} redirectTo="/">
              <Suspense fallback={<PageLoading />}>
                <CustomerCreatePage />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="customers/:id/edit"
          element={
            <RoleGuard allowedRoles={['DISPATCHER', 'ADMIN', 'SUPER_ADMIN']} redirectTo="/">
              <Suspense fallback={<PageLoading />}>
                <CustomerEditPage />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="customers/:id"
          element={
            <RoleGuard allowedRoles={['DISPATCHER', 'ADMIN', 'SUPER_ADMIN']} redirectTo="/">
              <Suspense fallback={<PageLoading />}>
                <CustomerDetailPage />
              </Suspense>
            </RoleGuard>
          }
        />

        {/* Contract Management */}
        <Route
          path="contracts"
          element={
            <ProtectedRoute roles={['ADMIN', 'SUPER_ADMIN', 'DISPATCHER']}>
              <Suspense fallback={<PageLoading />}>
                <ContractListPage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* User Management (Admin only) */}
        <Route
          path="users"
          element={
            <ProtectedRoute roles={['ADMIN', 'SUPER_ADMIN']}>
              <Suspense fallback={<PageLoading />}>
                <UserListPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="users/new"
          element={
            <ProtectedRoute roles={['ADMIN', 'SUPER_ADMIN']}>
              <Suspense fallback={<PageLoading />}>
                <UserCreatePage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="users/:id/edit"
          element={
            <ProtectedRoute roles={['ADMIN', 'SUPER_ADMIN']}>
              <Suspense fallback={<PageLoading />}>
                <UserEditPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="users/:id"
          element={
            <ProtectedRoute roles={['ADMIN', 'SUPER_ADMIN']}>
              <Suspense fallback={<PageLoading />}>
                <UserDetailPage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Reports - Admin and Dispatcher */}
        <Route
          path="reports"
          element={
            <RoleGuard allowedRoles={['DISPATCHER', 'ADMIN', 'SUPER_ADMIN']} redirectTo="/">
              <Suspense fallback={<PageLoading />}>
                <ReportsPage />
              </Suspense>
            </RoleGuard>
          }
        />

        {/* Invoice Management */}
        <Route
          path="invoices"
          element={
            <RoleGuard allowedRoles={['DISPATCHER', 'ADMIN', 'SUPER_ADMIN']} redirectTo="/">
              <Suspense fallback={<PageLoading />}>
                <InvoiceListPage />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="invoices/new"
          element={
            <RoleGuard allowedRoles={['DISPATCHER', 'ADMIN', 'SUPER_ADMIN']} redirectTo="/">
              <Suspense fallback={<PageLoading />}>
                <InvoiceCreatePage />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="invoices/:id"
          element={
            <RoleGuard allowedRoles={['DISPATCHER', 'ADMIN', 'SUPER_ADMIN']} redirectTo="/">
              <Suspense fallback={<PageLoading />}>
                <InvoiceDetailPage />
              </Suspense>
            </RoleGuard>
          }
        />

        {/* KVA Management */}
        <Route
          path="kvas"
          element={
            <RoleGuard allowedRoles={['DISPATCHER', 'ADMIN', 'SUPER_ADMIN']} redirectTo="/">
              <Suspense fallback={<PageLoading />}>
                <KVAListPage />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="kvas/new"
          element={
            <RoleGuard allowedRoles={['DISPATCHER', 'ADMIN', 'SUPER_ADMIN']} redirectTo="/">
              <Suspense fallback={<PageLoading />}>
                <KVACreatePage />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="kvas/:id"
          element={
            <RoleGuard allowedRoles={['DISPATCHER', 'ADMIN', 'SUPER_ADMIN']} redirectTo="/">
              <Suspense fallback={<PageLoading />}>
                <KVADetailPage />
              </Suspense>
            </RoleGuard>
          }
        />

        {/* Damage Case Management */}
        <Route
          path="damage"
          element={
            <RoleGuard allowedRoles={['DISPATCHER', 'ADMIN', 'SUPER_ADMIN']} redirectTo="/">
              <Suspense fallback={<PageLoading />}>
                <DamageCaseListPage />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="damage/new"
          element={
            <RoleGuard allowedRoles={['DISPATCHER', 'ADMIN', 'SUPER_ADMIN']} redirectTo="/">
              <Suspense fallback={<PageLoading />}>
                <DamageCaseCreatePage />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="damage/:id"
          element={
            <RoleGuard allowedRoles={['DISPATCHER', 'ADMIN', 'SUPER_ADMIN']} redirectTo="/">
              <Suspense fallback={<PageLoading />}>
                <DamageCaseDetailPage />
              </Suspense>
            </RoleGuard>
          }
        />

        {/* Repair Order Management */}
        <Route
          path="repair"
          element={
            <RoleGuard
              allowedRoles={['DISPATCHER', 'WORKSHOP', 'ADMIN', 'SUPER_ADMIN']}
              redirectTo="/"
            >
              <Suspense fallback={<PageLoading />}>
                <RepairOrderListPage />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="repair/new"
          element={
            <RoleGuard
              allowedRoles={['DISPATCHER', 'WORKSHOP', 'ADMIN', 'SUPER_ADMIN']}
              redirectTo="/"
            >
              <Suspense fallback={<PageLoading />}>
                <RepairOrderCreatePage />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="repair/:id"
          element={
            <RoleGuard
              allowedRoles={['DISPATCHER', 'WORKSHOP', 'ADMIN', 'SUPER_ADMIN']}
              redirectTo="/"
            >
              <Suspense fallback={<PageLoading />}>
                <RepairOrderDetailPage />
              </Suspense>
            </RoleGuard>
          }
        />

        {/* Insurance Case Management */}
        <Route
          path="insurance"
          element={
            <RoleGuard allowedRoles={['DISPATCHER', 'ADMIN', 'SUPER_ADMIN']} redirectTo="/">
              <Suspense fallback={<PageLoading />}>
                <InsuranceCaseListPage />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="insurance/new"
          element={
            <RoleGuard allowedRoles={['DISPATCHER', 'ADMIN', 'SUPER_ADMIN']} redirectTo="/">
              <Suspense fallback={<PageLoading />}>
                <InsuranceCaseCreatePage />
              </Suspense>
            </RoleGuard>
          }
        />
        <Route
          path="insurance/:id"
          element={
            <RoleGuard allowedRoles={['DISPATCHER', 'ADMIN', 'SUPER_ADMIN']} redirectTo="/">
              <Suspense fallback={<PageLoading />}>
                <InsuranceCaseDetailPage />
              </Suspense>
            </RoleGuard>
          }
        />

        {/* Settings */}
        <Route
          path="settings"
          element={
            <ProtectedRoute roles={['ADMIN', 'SUPER_ADMIN', 'DISPATCHER']}>
              <Suspense fallback={<PageLoading />}>
                <SettingsPage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Fahrzeugmodell-Katalog - Admin only (PUT erfordert ADMINS) */}
        <Route
          path="settings/vehicle-models"
          element={
            <ProtectedRoute roles={['ADMIN', 'SUPER_ADMIN']}>
              <Suspense fallback={<PageLoading />}>
                <VehicleModelCatalogPage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Location Management (Standorte) - Admin only (API mutations require ADMINS) */}
        <Route
          path="settings/locations"
          element={
            <ProtectedRoute roles={['ADMIN', 'SUPER_ADMIN']}>
              <Suspense fallback={<PageLoading />}>
                <LocationListPage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Yard Management - Admin and Dispatcher */}
        <Route
          path="settings/yard-zones"
          element={
            <ProtectedRoute roles={['ADMIN', 'SUPER_ADMIN', 'DISPATCHER']}>
              <Suspense fallback={<PageLoading />}>
                <YardZoneListPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="settings/yard-positions"
          element={
            <ProtectedRoute roles={['ADMIN', 'SUPER_ADMIN', 'DISPATCHER']}>
              <Suspense fallback={<PageLoading />}>
                <YardPositionListPage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

export default App;
